import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import VueVideoPlayer from '@videojs-player/vue'
import 'video.js/dist/video-js.css'
import App from './App'
import Main from './components/Main'
import VideoDetail from './components/VideoDetail'
import UserDetail from './components/UserDetail'
import Categories from './components/Categories'
import UserSettings from './components/UserSetting'
import UpdateVideo from './components/UpdateVideo'
import CategoryVideos from './components/CategoryVideos'
import TagVideos from './components/TagVideos'
import NotFound from './components/NotFound'
import Messages from './components/Messages'
import AdminUsers from './components/AdminUsers'
import AdminEmails from './components/AdminEmails'
import AdminPanel from './components/AdminPanel'
import AdminPendingVideos from './components/AdminPendingVideos'
import AdminImportUrl from './components/AdminImportUrl'
import ComingSoon from './components/ComingSoon'
import VueFeather from 'vue-feather';
import { useToast } from './composables/useToast';

import { init as initAnalytics, setupGlobalErrorTracking } from './utils/analytics';
import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';
import {
  applyPolyfills,
  defineCustomElements,
} from '@aws-amplify/ui-components/loader';
import AmplifyVue from '@aws-amplify/ui-vue';
import "./assets/eelam-theme.css";

// 2. Define some routes
// Each route should map to a component.
// We'll talk about nested routes later.
const routes = [
    { path: '/login', redirect: '/?login=1' },
    { path: '/', component: Main },
    { path: '/v/:id', component: VideoDetail },
    { path: '/watch/:id', component: VideoDetail },
    { path: '/@:username/:tab?', component: UserDetail },
    { path: '/categories', redirect: '/admin/categories' },
    { path: '/category/:id', component: CategoryVideos },
    { path: '/tag/:tagName', component: TagVideos },
    { path: '/settings', component: UserSettings },
    { path: '/settings/general', component: UserSettings },
    { path: '/settings/profile', component: UserSettings },
    { path: '/settings/avatar', component: UserSettings },
    { path: '/settings/verification', component: UserSettings },
    { path: '/settings/blocked', component: UserSettings },
    { path: '/settings/delete', component: UserSettings },
    { path: '/settings/password', component: UserSettings },
    { path: '/settings/two-factor', component: UserSettings },
    { path: '/settings/my-info', component: UserSettings },
    { path: '/finish/:id', component: UpdateVideo },
    { path: '/edit/:id', component: UpdateVideo },
    {
        path: '/admin',
        component: AdminPanel,
        children: [
            { path: '', redirect: '/admin/pending-videos' },
            { path: 'pending-videos', component: AdminPendingVideos },
            { path: 'import-url', component: AdminImportUrl },
            { path: 'categories', component: Categories },
            { path: 'users', component: AdminUsers },
            { path: 'emails', component: AdminEmails },
        ],
    },
    {
        path: '/studio',
        component: ComingSoon,
        meta: {
            title: 'Video studio',
            titleTA: 'வீடியோ அரங்கம்',
            description: 'Tools for managing uploads, drafts, and channel videos will be available here soon.',
            descriptionTA: 'பதிவேற்றங்கள், வரைவுகள் மற்றும் சேனல் காணொளிகளை நிர்வகிக்கும் கருவிகள் விரைவில் இங்கே கிடைக்கும்.',
            icon: 'video'
        }
    },
    {
        path: '/subscriptions',
        component: ComingSoon,
        meta: {
            title: 'Subscriptions',
            titleTA: 'சந்தாக்கள்',
            description: 'A dedicated page for followed channels and updates is coming soon.',
            descriptionTA: 'நீங்கள் பின்தொடரும் சேனல்கள் மற்றும் புதுப்பிப்புகளுக்கான தனிப் பக்கம் விரைவில் வருகிறது.',
            icon: 'layers'
        }
    },
    {
        path: '/articles',
        component: ComingSoon,
        meta: {
            title: 'My articles',
            titleTA: 'எனது கட்டுரைகள்',
            description: 'Article publishing and management tools are being prepared.',
            descriptionTA: 'கட்டுரைகளை வெளியிடவும் நிர்வகிக்கவும் பயன்படும் கருவிகள் தயாராகின்றன.',
            icon: 'file-text'
        }
    },
    {
        path: '/playlists',
        component: ComingSoon,
        meta: {
            title: 'Playlists',
            titleTA: 'பட்டியல்கள்',
            description: 'Playlist creation and management will be added soon.',
            descriptionTA: 'பட்டியல் உருவாக்கம் மற்றும் நிர்வகிப்பு விரைவில் சேர்க்கப்படும்.',
            icon: 'list'
        }
    },
    { path: '/history', component: Main },
    { path: '/latest', component: Main },
    { path: '/trending', component: Main },
    { path: '/top', component: Main },
    { path: '/popular-channels', component: Main },
    { path: '/popular', component: Main },
    { 
        path: '/messages', 
        component: Messages,
        meta: { requiresAuth: true },
        children: [
            {
                path: ':userId',
                name: 'MessagesWithUser',
                component: Messages,
                meta: { requiresAuth: true }
            }
        ]
    },
    { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
]
  
// 3. Create the router instance and pass the `routes` option
// You can pass in additional options here, but let's
// keep it simple for now.
const router = createRouter({
  // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
  history: createWebHistory(),
  routes, // short for `routes: routes`
  scrollBehavior() {
    return { top: 0 }
  },
})

const clickOutside = {
  beforeMount: (el, binding) => {
    el.clickOutsideEvent = event => {
      // here I check that click was outside the el and his children
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        binding.value();
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted: el => {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};
  
  
Amplify.configure({
  ...awsExports,
  aws_appsync_additionalAuthenticationTypes: 'AMAZON_COGNITO_USER_POOLS',
  Auth: {
    authenticationFlowType: 'USER_PASSWORD_AUTH'
  },
  Storage: {
    AWSS3: {
      bucket: 'eelamfiles160629-staging',
      region: 'us-west-2'
    }
  }
});
applyPolyfills().then(() => {
  defineCustomElements(window);
});
initAnalytics(router);

const app = createApp(App)
// app.config.compilerOptions.isCustomElement = tag => tag.startsWith('amplify-');
app.config.unwrapInjectedRef = true;
setupGlobalErrorTracking({ app, router });
app.use(AmplifyVue);
app.use(router);
app.use(VueVideoPlayer);
app.use()
app.config.globalProperties.$toast = useToast();
app.component(VueFeather.name, VueFeather);
app.mixin({
  methods: {
    timeAgo(date) {
      var seconds = Math.floor((new Date() - date) / 1000);
      const lang = this.languageRef || this.currentLanguage || 'EN';
      const ta = lang === 'TA';

      var interval = seconds / 31536000;

      if (interval > 1) {
        const n = Math.floor(interval);
        return ta ? `${n} ${n === 1 ? 'ஆண்டு' : 'ஆண்டுகள்'}` : n + (n === 1 ? " year" : " years");
      }
      interval = seconds / 2592000;
      if (interval > 1) {
        const n = Math.floor(interval);
        return ta ? `${n} ${n === 1 ? 'மாதம்' : 'மாதங்கள்'}` : n + (n === 1 ? " month" : " months");
      }
      interval = seconds / 86400;
      if (interval > 1) {
        const n = Math.floor(interval);
        return ta ? `${n} ${n === 1 ? 'நாள்' : 'நாட்கள்'}` : n + (n === 1 ? " day" : " days");
      }
      interval = seconds / 3600;
      if (interval > 1) {
        const n = Math.floor(interval);
        return ta ? `${n} மணி நேரம்` : n + (n === 1 ? " hour" : " hours");
      }
      interval = seconds / 60;
      if (interval > 1) {
        const n = Math.floor(interval);
        return ta ? `${n} ${n === 1 ? 'நிமிடம்' : 'நிமிடங்கள்'}` : n + (n === 1 ? " minute" : " minutes");
      }
      const n = Math.floor(seconds);
      return ta ? `${n} ${n === 1 ? 'விநாடி' : 'விநாடிகள்'}` : n + (n === 1 ? " second" : " seconds");
    },
    timeInSeconds(date) {
      return Math.floor((new Date() - date) / 1000);
    },
    isOnline(time) {
      return time > 3800
    },
    convertTime(time) {
      return new Date(time).toUTCString()
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
    },
    formattedDuration(duration) {
      const result = new Date(duration * 1000).toISOString().slice(14, 19);
      return result
    },
    debounce(func, timeout = 300){
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => { func.apply(this, args); }, timeout);
      };
    },
    displayName(owner) {
        if (owner.displayName != null && owner.displayName.length != 0) {
            return owner.displayName
        } else {
            return `${owner.firstName} ${owner.lastName}`
        }
    },
    downloadCoverImage(userId) {
        this.coverURL = `https://cdn1.eelam.tv/public/images/user/${userId}/cover.png`;
    },
    downloadProfileImage(userId) {
        this.avatarURL = `https://cdn1.eelam.tv/public/images/user/${userId}/avatar.png`;
    },
    userImage(userId) {
      return `${this.cdnDomain}/public/images/user/${userId}/avatar.png`
    },
    decodeHtml(text) {
      if (!text) return text;
      const el = document.createElement('textarea');
      text = text.replace("<br>", "\n");
      el.innerHTML = text;
      return el.value;
    },
    formatUserContent(content) {
      if (!content) return '';
      let text = this.decodeHtml(content);

      // Preserve safe HTML tags before escaping
      const safeTags = [];
      const safeTagPattern = /<(\/?\s*(?:br|p|b|i|em|strong|u|s|strike|blockquote|ul|ol|li|h[1-6]))\s*\/?>/gi;
      text = text.replace(safeTagPattern, (match, tag) => {
        const idx = safeTags.length;
        const normalized = tag.trim().toLowerCase();
        const isClosing = normalized.startsWith('/');
        const tagName = normalized.replace('/', '');
        safeTags.push(isClosing ? '</' + tagName + '>' : (tagName === 'br' ? '<br>' : '<' + tagName + '>'));
        return '{{SAFE' + idx + '}}';
      });

      // Escape remaining HTML to prevent XSS
      text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // Restore safe tags
      text = text.replace(/\{\{SAFE(\d+)\}\}/g, function(m, idx) { return safeTags[parseInt(idx)]; });

      const decodeUrl = (url) => {
        try {
          let decoded = url;
          let max = 5;
          while (decoded.includes('%25') && max-- > 0) {
            decoded = decodeURIComponent(decoded);
          }
          return decodeURIComponent(decoded);
        } catch { return url; }
      };

      const makeLinkLabel = (url) => {
        try {
          const u = new URL(url);
          if (u.hostname.includes('eelam.tv')) {
            const path = decodeURIComponent(u.pathname.replace(/^\/watch\//, '').replace(/\.html$/, '').replace(/-/g, ' ')).substring(0, 60);
            return path ? 'Watch: ' + path : url;
          }
          return (u.hostname + u.pathname).substring(0, 60);
        } catch { return url.substring(0, 60); }
      };

      // BBCode [a]url[/a]
      text = text.replace(/\[a\](.*?)\[\/a\]/gi, (m, url) => {
        const d = decodeUrl(url);
        return `<a href="${d}" target="_blank" rel="noopener" class="user-content-link">${makeLinkLabel(d)}</a>`;
      });

      // BBCode [url=...]text[/url] and [url]text[/url]
      text = text.replace(/\[url=?(.*?)\](.*?)\[\/url\]/gi, (m, url, t) => {
        const href = decodeUrl(url || t);
        return `<a href="${href}" target="_blank" rel="noopener" class="user-content-link">${t || makeLinkLabel(href)}</a>`;
      });

      // Basic formatting
      text = text.replace(/\[b\](.*?)\[\/b\]/gi, '<strong>$1</strong>');
      text = text.replace(/\[i\](.*?)\[\/i\]/gi, '<em>$1</em>');
      text = text.replace(/\[img\](.*?)\[\/img\]/gi, '');
      text = text.replace(/\[\/?[a-z]+\]/gi, '');

      // Auto-link plain URLs not already in an <a> tag
      text = text.replace(/(^|[^"=])(https?:\/\/[^\s<]+)/gi, '$1<a href="$2" target="_blank" rel="noopener" class="user-content-link">$2</a>');

      // Convert newlines to <br> (after all other processing)
      text = text.replace(/\n/g, '<br>');

      return text;
    },
    stripBBCode(content) {
      if (!content) return '';
      let text = this.decodeHtml(content);
      text = text.replace(/\[a\](.*?)\[\/a\]/gi, (m, url) => {
        try {
          let d = url; let max = 5;
          while (d.includes('%25') && max-- > 0) d = decodeURIComponent(d);
          return decodeURIComponent(d);
        } catch { return url; }
      });
      text = text.replace(/\[url=?(.*?)\](.*?)\[\/url\]/gi, '$2');
      text = text.replace(/\[\/?[a-z]+\]/gi, '');
      return text;
    }
  },

  data: function() {
    return {
      cdnDomain: 'https://cdn1.eelam.tv',
      s3Domain: 'https://eelamfiles160629-staging.s3.us-west-2.amazonaws.com',
      isAdmin: false,
      currentUser: null,
      coverURL: null,
      sidebarRoutes: [
          {
              title: "Home",
              titleTA: "முகப்பு",
              icon: "home",
              route: "/"
          },
          {
              title: "History",
              titleTA: "வரலாறு",
              icon: "compass",
              route: "/history"
          },
          {
              title: "Latest videos",
              titleTA: "சமீபத்தியவை",
              icon: "video",
              route: "/latest"
          },
          {
              title: "Trending",
              titleTA: "பிரபலமானவை",
              icon: "trending-up",
              route: "/trending"
          },
          {
              title: "Top videos",
              titleTA: "சிறந்தவை",
              icon: "bar-chart",
              route: "/top"
          },
          {
              title: "Popular channels",
              titleTA: "பிரபலங்கள்",
              icon: "star",
              route: "/popular-channels"
          },
      ],
    countries: [
      {value: "0", label: "Select Country"},
      {value: "1", label: "United States"},
      {value: "2", label: "Canada"},
      {value: "3", label: "Afghanistan"},
      {value: "4", label: "Albania"},
      {value: "5", label: "Algeria"},
      {value: "6", label: "American Samoa"},
      {value: "7", label: "Andorra"},
      {value: "8", label: "Angola"},
      {value: "9", label: "Anguilla"},
      {value: "10", label: "Antarctica"},
      {value: "11", label: "Antigua and/or Barbuda"},
      {value: "12", label: "Argentina"},
      {value: "13", label: "Armenia"},
      {value: "14", label: "Aruba"},
      {value: "15", label: "Australia"},
      {value: "16", label: "Austria"},
      {value: "17", label: "Azerbaijan"},
      {value: "18", label: "Bahamas"},
      {value: "19", label: "Bahrain"},
      {value: "20", label: "Bangladesh"},
      {value: "21", label: "Barbados"},
      {value: "22", label: "Belarus"},
      {value: "23", label: "Belgium"},
      {value: "24", label: "Belize"},
      {value: "25", label: "Benin"},
      {value: "26", label: "Bermuda"},
      {value: "27", label: "Bhutan"},
      {value: "28", label: "Bolivia"},
      {value: "29", label: "Bosnia and Herzegovina"},
      {value: "30", label: "Botswana"},
      {value: "31", label: "Bouvet Island"},
      {value: "32", label: "Brazil"},
      {value: "34", label: "Brunei Darussalam"},
      {value: "35", label: "Bulgaria"},
      {value: "36", label: "Burkina Faso"},
      {value: "37", label: "Burundi"},
      {value: "38", label: "Cambodia"},
      {value: "39", label: "Cameroon"},
      {value: "40", label: "Cape Verde"},
      {value: "41", label: "Cayman Islands"},
      {value: "42", label: "Central African Republic"},
      {value: "43", label: "Chad"},
      {value: "44", label: "Chile"},
      {value: "45", label: "China"},
      {value: "46", label: "Christmas Island"},
      {value: "47", label: "Cocos (Keeling) Islands"},
      {value: "48", label: "Colombia"},
      {value: "49", label: "Comoros"},
      {value: "50", label: "Congo"},
      {value: "51", label: "Cook Islands"},
      {value: "52", label: "Costa Rica"},
      {value: "53", label: "Croatia (Hrvatska)"},
      {value: "54", label: "Cuba"},
      {value: "55", label: "Cyprus"},
      {value: "56", label: "Czech Republic"},
      {value: "57", label: "Denmark"},
      {value: "58", label: "Djibouti"},
      {value: "59", label: "Dominica"},
      {value: "60", label: "Dominican Republic"},
      {value: "61", label: "East Timor"},
      {value: "62", label: "Ecuador"},
      {value: "63", label: "Egypt"},
      {value: "64", label: "El Salvador"},
      {value: "65", label: "Equatorial Guinea"},
      {value: "66", label: "Eritrea"},
      {value: "67", label: "Estonia"},
      {value: "68", label: "Ethiopia"},
      {value: "69", label: "Falkland Islands (Malvinas)"},
      {value: "70", label: "Faroe Islands"},
      {value: "71", label: "Fiji"},
      {value: "72", label: "Finland"},
      {value: "73", label: "France"},
      {value: "74", label: "France, Metropolitan"},
      {value: "75", label: "French Guiana"},
      {value: "76", label: "French Polynesia"},
      {value: "77", label: "French Southern Territories"},
      {value: "78", label: "Gabon"},
      {value: "79", label: "Gambia"},
      {value: "80", label: "Georgia"},
      {value: "81", label: "Germany"},
      {value: "82", label: "Ghana"},
      {value: "83", label: "Gibraltar"},
      {value: "84", label: "Greece"},
      {value: "85", label: "Greenland"},
      {value: "86", label: "Grenada"},
      {value: "87", label: "Guadeloupe"},
      {value: "88", label: "Guam"},
      {value: "89", label: "Guatemala"},
      {value: "90", label: "Guinea"},
      {value: "91", label: "Guinea-Bissau"},
      {value: "92", label: "Guyana"},
      {value: "93", label: "Haiti"},
      {value: "94", label: "Heard and Mc Donald Islands"},
      {value: "95", label: "Honduras"},
      {value: "96", label: "Hong Kong"},
      {value: "97", label: "Hungary"},
      {value: "98", label: "Iceland"},
      {value: "99", label: "India"},
      {value: "100", label: "Indonesia"},
      {value: "101", label: "Iran (Islamic Republic of)"},
      {value: "102", label: "Iraq"},
      {value: "103", label: "Ireland"},
      {value: "104", label: "Israel"},
      {value: "105", label: "Italy"},
      {value: "106", label: "Ivory Coast"},
      {value: "107", label: "Jamaica"},
      {value: "108", label: "Japan"},
      {value: "109", label: "Jordan"},
      {value: "110", label: "Kazakhstan"},
      {value: "111", label: "Kenya"},
      {value: "112", label: "Kiribati"},
      {value: "113", label: "Korea, Democratic People's Republic of"},
      {value: "114", label: "Korea, Republic of"},
      {value: "115", label: "Kosovo"},
      {value: "116", label: "Kuwait"},
      {value: "117", label: "Kyrgyzstan"},
      {value: "118", label: "Lao People's Democratic Republic"},
      {value: "119", label: "Latvia"},
      {value: "120", label: "Lebanon"},
      {value: "121", label: "Lesotho"},
      {value: "122", label: "Liberia"},
      {value: "123", label: "Libyan Arab Jamahiriya"},
      {value: "124", label: "Liechtenstein"},
      {value: "125", label: "Lithuania"},
      {value: "126", label: "Luxembourg"},
      {value: "127", label: "Macau"},
      {value: "128", label: "Macedonia"},
      {value: "129", label: "Madagascar"},
      {value: "130", label: "Malawi"},
      {value: "131", label: "Malaysia"},
      {value: "132", label: "Maldives"},
      {value: "133", label: "Mali"},
      {value: "134", label: "Malta"},
      {value: "135", label: "Marshall Islands"},
      {value: "136", label: "Martinique"},
      {value: "137", label: "Mauritania"},
      {value: "138", label: "Mauritius"},
      {value: "139", label: "Mayotte"},
      {value: "140", label: "Mexico"},
      {value: "141", label: "Micronesia, Federated States of"},
      {value: "142", label: "Moldova, Republic of"},
      {value: "143", label: "Monaco"},
      {value: "144", label: "Mongolia"},
      {value: "145", label: "Montenegro"},
      {value: "146", label: "Montserrat"},
      {value: "147", label: "Morocco"},
      {value: "148", label: "Mozambique"},
      {value: "149", label: "Myanmar"},
      {value: "150", label: "Namibia"},
      {value: "151", label: "Nauru"},
      {value: "152", label: "Nepal"},
      {value: "153", label: "Netherlands"},
      {value: "154", label: "Netherlands Antilles"},
      {value: "155", label: "New Caledonia"},
      {value: "156", label: "New Zealand"},
      {value: "157", label: "Nicaragua"},
      {value: "158", label: "Niger"},
      {value: "159", label: "Nigeria"},
      {value: "160", label: "Niue"},
      {value: "161", label: "Norfork Island"},
      {value: "162", label: "Northern Mariana Islands"},
      {value: "163", label: "Norway"},
      {value: "164", label: "Oman"},
      {value: "165", label: "Pakistan"},
      {value: "166", label: "Palau"},
      {value: "167", label: "Panama"},
      {value: "168", label: "Papua New Guinea"},
      {value: "169", label: "Paraguay"},
      {value: "170", label: "Peru"},
      {value: "171", label: "Philippines"},
      {value: "172", label: "Pitcairn"},
      {value: "173", label: "Poland"},
      {value: "174", label: "Portugal"},
      {value: "175", label: "Puerto Rico"},
      {value: "176", label: "Qatar"},
      {value: "177", label: "Reunion"},
      {value: "178", label: "Romania"},
      {value: "179", label: "Russian Federation"},
      {value: "180", label: "Rwanda"},
      {value: "181", label: "Saint Kitts and Nevis"},
      {value: "182", label: "Saint Lucia"},
      {value: "183", label: "Saint Vincent and the Grenadines"},
      {value: "184", label: "Samoa"},
      {value: "185", label: "San Marino"},
      {value: "186", label: "Sao Tome and Principe"},
      {value: "187", label: "Saudi Arabia"},
      {value: "188", label: "Senegal"},
      {value: "189", label: "Serbia"},
      {value: "190", label: "Seychelles"},
      {value: "191", label: "Sierra Leone"},
      {value: "192", label: "Singapore"},
      {value: "193", label: "Slovakia"},
      {value: "194", label: "Slovenia"},
      {value: "195", label: "Solomon Islands"},
      {value: "196", label: "Somalia"},
      {value: "197", label: "South Africa"},
      {value: "198", label: "South Georgia South Sandwich Islands"},
      {value: "199", label: "Spain"},
      {value: "200", label: "Sri Lanka"},
      {value: "201", label: "St. Helena"},
      {value: "202", label: "St. Pierre and Miquelon"},
      {value: "203", label: "Sudan"},
      {value: "204", label: "Suriname"},
      {value: "205", label: "Svalbarn and Jan Mayen Islands"},
      {value: "206", label: "Swaziland"},
      {value: "207", label: "Sweden"},
      {value: "208", label: "Switzerland"},
      {value: "209", label: "Syrian Arab Republic"},
      {value: "210", label: "Taiwan"},
      {value: "211", label: "Tajikistan"},
      {value: "212", label: "Tanzania, United Republic of"},
      {value: "213", label: "Thailand"},
      {value: "214", label: "Togo"},
      {value: "215", label: "Tokelau"},
      {value: "216", label: "Tonga"},
      {value: "217", label: "Trinidad and Tobago"},
      {value: "218", label: "Tunisia"},
      {value: "219", label: "Turkey"},
      {value: "220", label: "Turkmenistan"},
      {value: "221", label: "Turks and Caicos Islands"},
      {value: "222", label: "Tuvalu"},
      {value: "223", label: "Uganda"},
      {value: "224", label: "Ukraine"},
      {value: "225", label: "United Arab Emirates"},
      {value: "226", label: "United Kingdom"},
      {value: "227", label: "United States minor outlying islands"},
      {value: "228", label: "Uruguay"},
      {value: "229", label: "Uzbekistan"},
      {value: "230", label: "Vanuatu"},
      {value: "231", label: "Vatican City State"},
      {value: "232", label: "Venezuela"},
      {value: "233", label: "Vietnam"},
      {value: "238", label: "Yemen"},
      {value: "239", label: "Yugoslavia"},
      {value: "240", label: "Zaire"},
      {value: "241", label: "Zambia"},
      {value: "242", label: "Zimbabwe"}
    ]
    }
  }
})
app.directive("click-outside", clickOutside)
app.mount('#app')
